"use client";

import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Facebook, Instagram, Youtube, Linkedin, Phone, Mail, Music as TikTok, MapPin, ArrowRight } from 'lucide-react';
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-32 bg-white/5 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="group flex items-center text-sm font-sans text-white/60 hover:text-primary transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]">
          <ArrowRight className="size-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-[var(--duration-fast)]" />
          {item.label}
        </Link>)}
    </nav>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <footer id="footer" className="text-white border-t border-primary/20 !bg-[hsl(24,10%,10%)]" aria-label="Phoenix Enterprise Footer">
      <div className="container mx-auto px-6 max-w-7xl pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          
          {}
          <div className="flex flex-col items-start space-y-8">
            <Link to="/">
              <WvcLogo className="h-10 w-auto" />
            </Link>
            <p className="text-sm font-sans leading-relaxed text-white/50 max-w-xs">
              Phoenix Enterprise TCP defines the pinnacle of professional mobility in Los Angeles. We deliver a seamless blend of luxury, punctuality, and elite chauffeur excellence for our distinguished clientele.
            </p>
            <div className="flex flex-wrap gap-4">
              {[{
              icon: Facebook,
              href: "https://www.facebook.com/reel/1326795919211492",
              label: "Facebook"
            }, {
              icon: Instagram,
              href: "https://www.instagram.com/phoenixlimousineservice/",
              label: "Instagram"
            }, {
              icon: TikTok,
              href: "https://www.tiktok.com/@phoenixlimousineservice",
              label: "TikTok"
            }, {
              icon: Youtube,
              href: "https://youtube.com",
              label: "YouTube"
            }, {
              icon: Linkedin,
              href: "https://linkedin.com",
              label: "LinkedIn"
            }].map((social, i) => <Link key={social.label} to={social.href} newTab data-index={i} className="p-2.5 rounded-md bg-white/5 border border-white/10 text-white/70 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-[var(--duration-fast)]" aria-label={social.label}>
                  <social.icon className="size-4" />
                </Link>)}
            </div>
          </div>

          {}
          <div className="flex flex-col">
            <h3 className="font-display text-sm font-bold mb-8 text-primary uppercase tracking-[0.2em]">
              Navigation
            </h3>
            <MenuProvider menu_id="42">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col">
            <h3 className="font-display text-sm font-bold mb-8 text-primary uppercase tracking-[0.2em]">
              Concierge
            </h3>
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-black transition-colors duration-[var(--duration-fast)]">
                  <Phone className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-white/30 uppercase font-bold tracking-widest mb-1">24/7 Support</span>
                  <Link to="tel:3234926292" className="text-sm hover:text-primary transition-colors font-medium text-white">323-492-6292</Link>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-black transition-colors duration-[var(--duration-fast)]">
                  <Mail className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-white/30 uppercase font-bold tracking-widest mb-1">Email Inquiry</span>
                  <Link to="mailto:phoenixenerprises@gmail.com" className="text-sm hover:text-primary transition-colors break-all font-medium text-white">phoenixenerprises@gmail.com</Link>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-black transition-colors duration-[var(--duration-fast)]">
                  <MapPin className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-white/30 uppercase font-bold tracking-widest mb-1">Service Hub</span>
                  <span className="text-sm font-medium text-white">Los Angeles, CA</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col">
            <h3 className="font-display text-sm font-bold mb-8 text-primary uppercase tracking-[0.2em]">
              Our Promise
            </h3>
            <div className="relative p-6 rounded-md bg-white/5 border border-white/10 overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-[var(--duration-fast)]" />
              <p className="text-sm font-sans leading-relaxed text-white/60 italic relative z-10">
                "To provide the most reliable and luxurious transportation experience in Southern California, ensuring every client arrives in style and on time."
              </p>
              <div className="mt-4 flex items-center gap-2">
                <div className="h-px w-8 bg-primary/30" />
                <span className="text-[10px] uppercase tracking-widest text-primary font-bold">Excellence Guaranteed</span>
              </div>
            </div>
          </div>
        </div>

        <Separator className="w-full mb-10 bg-white/5" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-sans text-white/30 uppercase tracking-[0.2em]">
          <p className="text-center md:text-left">
            &copy; {currentYear} Phoenix Enterprise TCP. <span className="hidden md:inline">|</span> <br className="md:hidden" /> All Rights Reserved.
          </p>
          <div className="flex gap-10">
            <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>;
}